'use client'

import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'

interface LazyBackgroundImageProps {
  src: string
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  initial?: any
  whileInView?: any
  viewport?: any
  transition?: any
}

export default function LazyBackgroundImage({
  src,
  children,
  className = '',
  style = {},
  initial,
  whileInView,
  viewport,
  transition,
}: LazyBackgroundImageProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef<HTMLLIElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsIntersecting(true)
          observer.disconnect()
        }
      },
      { rootMargin: '100px' } // Load earlier to prevent visible loading
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isIntersecting && !isLoaded) {
      const img = new Image()
      img.onload = () => setIsLoaded(true)
      img.src = src
    }
  }, [isIntersecting, src, isLoaded])

  // Always show the background image, but load it lazily
  const backgroundStyle = isLoaded
    ? { ...style, backgroundImage: `url('${src}')` }
    : { ...style, backgroundImage: `url('${src}')` } // Show image immediately, lazy loading is just for performance

  return (
    <motion.li
      ref={ref}
      className={className}
      style={backgroundStyle}
      initial={initial}
      whileInView={whileInView}
      viewport={viewport}
      transition={transition}
    >
      {children}
    </motion.li>
  )
}
